import { Button } from '@/components/ui';
import { useCreateBankTransaction } from '@/hooks/use-bank-transactions';
import { useToast } from '@/hooks/use-toast';
import type { BankDirection } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface BankTransactionFormProps {
  onSuccess: () => void;
  onCancel?: (() => void) | undefined;
}

const DIRECTIONS: BankDirection[] = ['IN', 'OUT'];

/**
 * Kézi banki tétel rögzítés űrlap (35-kör Fázis-1).
 * A `@kgc/api-contract` CreateBankTransaction wire-szerződésre épül (NINCS FE-kézi-zod).
 */
export function BankTransactionForm({ onSuccess, onCancel }: BankTransactionFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { create, isLoading } = useCreateBankTransaction();

  const [bookingDate, setBookingDate] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [direction, setDirection] = useState<BankDirection>('IN');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [counterpartyAccount, setCounterpartyAccount] = useState('');
  const [memo, setMemo] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await create({
        bookingDate: new Date(bookingDate).toISOString(),
        amount,
        direction,
        counterpartyName: counterpartyName || undefined,
        counterpartyAccount: counterpartyAccount || undefined,
        memo: memo || undefined,
      });
      toast({ title: t('bank.form.success'), variant: 'default' });
      onSuccess();
    } catch {
      toast({ title: t('bank.form.error'), variant: 'destructive' });
    }
  };

  const isDisabled = !bookingDate || amount <= 0 || isLoading;

  return (
    <form onSubmit={e => void handleSubmit(e)} className="space-y-4" data-testid="bank-form">
      <div>
        <label className="mb-2 block text-sm font-medium">{t('bank.form.bookingDate')}</label>
        <input
          type="date"
          data-testid="bank-bookingdate-input"
          value={bookingDate}
          onChange={e => setBookingDate(e.target.value)}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('bank.form.direction')}</label>
        <div className="grid grid-cols-2 gap-2">
          {DIRECTIONS.map(d => (
            <button
              key={d}
              type="button"
              data-testid={`bank-direction-${d.toLowerCase()}`}
              disabled={isLoading}
              onClick={() => setDirection(d)}
              className={`rounded-lg border-2 p-3 text-center transition-all ${
                direction === d ? 'border-primary bg-primary/10' : 'border-input hover:border-input'
              }`}
            >
              <p className="text-sm font-medium">{t(`bank.direction.${d}`)}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('bank.form.amount')}</label>
        <input
          type="number"
          data-testid="bank-amount-input"
          value={amount || ''}
          onChange={e => setAmount(Number(e.target.value) || 0)}
          min={0}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('bank.form.counterpartyName')}</label>
        <input
          type="text"
          data-testid="bank-counterparty-input"
          value={counterpartyName}
          onChange={e => setCounterpartyName(e.target.value)}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">
          {t('bank.form.counterpartyAccount')}
        </label>
        <input
          type="text"
          data-testid="bank-account-input"
          value={counterpartyAccount}
          onChange={e => setCounterpartyAccount(e.target.value)}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">{t('bank.form.memo')}</label>
        <textarea
          data-testid="bank-memo-input"
          value={memo}
          onChange={e => setMemo(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
        />
      </div>

      <div className="flex gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 rounded-md border border-input px-4 py-2 text-sm"
          >
            {t('common.cancel')}
          </button>
        )}
        <Button
          type="submit"
          data-testid="bank-submit"
          disabled={isDisabled}
          className="flex-1 bg-primary hover:bg-primary/90"
        >
          {t('bank.form.submit')}
        </Button>
      </div>
    </form>
  );
}
